import { CurrencyEnum } from "@finway-group/shared/lib/models"
import { mapIvalToMonths } from "@finway-group/shared/lib/utils/ival"
import { Alert, Button, Card, Col, Row, Tag } from "antd"
import moment from "moment"
import React from "react"
import { useTranslation } from "react-i18next"

import { ProgressBar } from "Components/ProgressBar"
import { CubeIcon, CubeXLIcon, LinesIcon, RocketIcon } from "Components/icons"
import { IconBackground } from "Components/icons/iconBackground"
import Loading from "Components/loading"
import PriceLabel from "Components/priceLabel"
import { getLanguageCode } from "Shared/hooks/i18n.hooks"
import { useBillingInvoiceConsumption, useBillingSettings } from "Shared/queries/billing.queries"
import { PaymentSettingsTabRoutes, RoutePathEnum } from "Shared/router/RoutePath.enum"

import { BillingSettingsCardPricesCard } from "./billingSettingsCardPrices.card"
import { BillingSettingsPeriodCard } from "./billingSettingsPeriod.card"

interface BillingSettingsProps {
    setActiveTab: (activeTab: PaymentSettingsTabRoutes) => void
}

export const BillingSettings = ({ setActiveTab }: BillingSettingsProps) => {
    const { t } = useTranslation()
    const locale = getLanguageCode()
    const { data: billingSettings, isFetching: isFetchingBillingSettings } = useBillingSettings({})
    const { data: billingInvoiceConsumption } = useBillingInvoiceConsumption({})

    const contractLineItems = billingSettings?.contract?.lineItems
    const contractFirstLineItem = contractLineItems && contractLineItems.length > 0 ? contractLineItems[0] : undefined
    const contractPlanDiscount = contractFirstLineItem?.discount
        ? {
              currency: contractFirstLineItem?.discount.currency,
              off: contractFirstLineItem?.discount.off,
              startDate: contractFirstLineItem?.startDate,
              durationInMonths: contractFirstLineItem?.termInMonths,
          }
        : undefined

    const subscription = billingSettings?.subscription
    const subscriptionIval = subscription?.interval ?? contractFirstLineItem?.interval!
    const subscriptionPlan = subscription?.paymentProductPlanId ?? contractFirstLineItem?.productId
    const subscriptionPlanDiscount = subscription?.planDiscount ?? (!subscription ? contractPlanDiscount : undefined)
    const calculateSubsciptionUnits = () => {
        const units = parseInt(subscriptionPlan?.metadata?.units ?? "0", 10)
        let multiplier = 0
        try {
            multiplier = mapIvalToMonths(subscriptionIval)
        } catch (err) {
            multiplier = 1
        }
        return units * multiplier
    }
    const subscriptionUnits = calculateSubsciptionUnits()
    const subscriptionPrice = subscription?.paymentProductPlanPriceRef ?? contractFirstLineItem?.priceRef

    const renderPriceLabel = () => {
        const subscriptionPriceAmount = (subscriptionPrice?.amount ?? 1) / 100
        const subscriptionPriceCurrency = subscriptionPrice?.currency ?? CurrencyEnum.EUR

        if (!subscriptionPlanDiscount) {
            return <PriceLabel value={subscriptionPriceAmount} currency={subscriptionPriceCurrency} />
        }
        return (
            <>
                <span className="mr-10">
                    <PriceLabel
                        value={
                            subscriptionPlanDiscount.currency
                                ? subscriptionPriceAmount - subscriptionPlanDiscount.off / 100
                                : subscriptionPriceAmount * ((100 - subscriptionPlanDiscount.off) / 100)
                        }
                        currency={subscriptionPriceCurrency}
                    />
                </span>
                <span className="line-through">
                    <PriceLabel value={subscriptionPriceAmount} currency={subscriptionPriceCurrency} />
                </span>
            </>
        )
    }

    const renderPlanCard = (button = <></>) => (
        <Card>
            <Row gutter={[16, 16]}>
                <Col flex={1} className="flex items-center space-x-10">
                    <IconBackground>
                        <CubeIcon />
                    </IconBackground>
                    <p className="text-lg text-primary">
                        {subscriptionPlan?.metadata[`${locale}_name`] || subscriptionPlan?.name}
                        {subscriptionPlanDiscount && (
                            <Tag color="green" className="ml-10">
                                {`${t("label:payment_settings.discount")}: `}
                                {subscriptionPlanDiscount.currency ? (
                                    <PriceLabel value={subscriptionPlanDiscount.off / 100} currency={subscriptionPlanDiscount.currency} />
                                ) : (
                                    `${subscriptionPlanDiscount.off}%`
                                )}
                                {subscriptionPlanDiscount.durationInMonths &&
                                    ` - ${t("label:payment_settings.discount_until")} ${moment(subscriptionPlanDiscount.startDate)
                                        .add(subscriptionPlanDiscount.durationInMonths, "months")
                                        .subtract(1, "days")
                                        .format("L")}`}
                            </Tag>
                        )}
                    </p>
                </Col>
                <Col flex={1} className="flex justify-end items-center">
                    <p className="text-xl font-semibold text-primary">{renderPriceLabel()}</p>
                </Col>
            </Row>
            <Row gutter={[16, 16]}>
                <Col flex={1} className="flex items-center flex-wrap">
                    <div className="basis-full">
                        <p className="text-gray-900">{t("label:payment_settings.usage_consumption")}</p>
                    </div>
                    <div className="block w-full">
                        <ProgressBar
                            currency={subscriptionPrice?.currency ?? CurrencyEnum.EUR}
                            value={billingInvoiceConsumption?.invoiceConsumption ?? 0}
                            total={subscriptionUnits}
                            thin
                            extended
                            includeValuesBelowBar
                            hideLabels
                        />
                    </div>
                </Col>
                <Col flex={1} className="flex justify-end items-center space-x-10 flex-wrap">
                    {button}
                </Col>
            </Row>
        </Card>
    )

    //
    // Render Logic
    //

    if (isFetchingBillingSettings) {
        return <Loading />
    }

    // Customer is subscribed
    if (subscription) {
        // if there is a schedule attached the customer should not upgrade
        return (
            <>
                {renderPlanCard()}
                <BillingSettingsPeriodCard
                    subscriptionInterval={subscriptionIval}
                    subStartDate={subscription?.startDate}
                    subEndDate={subscription?.endDate}
                    contractStartDate={contractFirstLineItem?.startDate!}
                />
                <BillingSettingsCardPricesCard subscriptionInterval={subscriptionIval} />
            </>
        )
    }

    // Customer has hubspot quote (aka contract) but is not signed up in stripe yet
    if (contractFirstLineItem) {
        return (
            <>
                <Row className="mb-10">
                    <Col span={24}>
                        <Alert
                            message={<span className="font-bold">{t("notification:payment_settings.automatic_billing.title")}</span>}
                            description={
                                <>
                                    <p>{t("notification:payment_settings.automatic_billing.message1")}</p>
                                    <p>{t("notification:payment_settings.automatic_billing.message2")}</p>
                                </>
                            }
                            type="info"
                            showIcon={true}
                        />
                    </Col>
                </Row>
                {renderPlanCard(
                    <Button icon={<RocketIcon />} className="ant-btn btn-highlight-green-active" onClick={() => setActiveTab(RoutePathEnum.SETTINGS_BILLING_TAB_PAYMENT_METHOD)}>
                        {t("action:payment_settings.sign_up")}
                    </Button>,
                )}
                <BillingSettingsCardPricesCard subscriptionInterval={subscriptionIval} />
            </>
        )
    }

    // Customer has no subscription
    return (
        <Row justify="space-around">
            <Col className="flex flex-col items-center">
                <div className="relative flex items-center my-20">
                    {/* TODO FA-22: use correct theme color classes for icons */}
                    <CubeXLIcon className="absolute mx-auto inset-x-0 finway-primary-text" style={{ width: 210, height: 210 }} />
                    <LinesIcon style={{ color: "#F3F3F3" }} />
                </div>
                <h2 className="mt-20 mb-10">{t("label:payment_settings.trial_upgrade_header")}</h2>
                <p className="mb-10 text-sm text-text leading-none">{t("label:payment_settings.trial_upgrade_message")}</p>
            </Col>
        </Row>
    )
}
